(() => {
  'use strict';
  const configNode = document.querySelector('[data-astra-cart-config]');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent), messages = config.messages;
  let pending = false, uncertain = false;
  const fail = (message, isUncertain = false) => Object.assign(new Error(message), {uncertain:isUncertain});
  const rootURL = () => window.theme?.Cart?.rootURL || config.cartUrl;
  async function request(url, options = {}, mutation = false) {
    const abort = new AbortController(), timer = setTimeout(() => abort.abort(),20000);
    try {
      const response = await fetch(url,{credentials:'same-origin',cache:'no-store',...options,signal:abort.signal});
      let data;
      try {data = await response.json();} catch (_) {throw fail(mutation ? messages.uncertain : messages.error,mutation);}
      if (!response.ok) throw fail((typeof data.description === 'string' ? `${messages.error} ${data.description}` : messages.error),mutation && response.status >= 500);
      return data;
    } catch (error) {
      if ('uncertain' in error) throw error;
      throw fail(mutation ? messages.uncertain : messages.error,mutation);
    } finally {clearTimeout(timer);}
  }
  const canonical = object => JSON.stringify(Object.entries(object || {}).sort(([a],[b]) => a.localeCompare(b)));
  const draftKey = key => `astra:edit:${key}`;
  function feedback(message, bad = false) {
    document.querySelectorAll('[data-astra-cart-feedback]').forEach(node => {node.textContent=message;node.hidden=false;node.setAttribute('role',bad ? 'alert' : 'status');});
  }
  function lock(value) {
    document.querySelectorAll('[data-astra-cart]').forEach(root => {
      root.setAttribute('aria-busy',String(value));
      root.querySelectorAll('button,input').forEach(control => {control.disabled=value;});
    });
  }
  const api = window.AstraCart = {
    messages,
    async read() {
      const data = await request(`${rootURL()}.js`);
      if (!Array.isArray(data.items)) throw fail(messages.error);
      window.theme?.Cart?.setCurrentData(data);
      return data;
    },
    async change(key, quantity, properties, snapshot, removeProperties = []) {
      if (typeof key !== 'string' || !key.includes(':')) throw fail(messages.stale);
      if (!Number.isSafeInteger(quantity) || quantity < 0) throw fail(messages.quantity_error);
      const before = await this.read(), line = before.items.find(item => item.key === key);
      if (!line) throw fail(messages.stale);
      if (snapshot && (line.quantity !== snapshot.quantity || canonical(line.properties) !== canonical(snapshot.properties) || line.variant_id !== snapshot.variant_id)) throw fail(messages.stale);
      const payload = {id:key,quantity};
      if (properties) {
        payload.properties = {...line.properties,...properties};
        for (const key of removeProperties) delete payload.properties[key];
      }
      const response = await request(`${rootURL()}/change.js`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)},true);
      if (!Array.isArray(response.items)) throw fail(messages.uncertain,true);
      let after;
      try {after=await this.read();} catch (_) {throw fail(messages.uncertain,true);}
      if (quantity === 0) {if (after.items.some(item => item.key === key)) throw fail(messages.uncertain,true);}
      else {
        const expected = properties ? payload.properties : line.properties;
        const matching = item => item.variant_id === line.variant_id && canonical(item.properties) === canonical(expected) && (item.selling_plan_allocation?.selling_plan?.id || null) === (line.selling_plan_allocation?.selling_plan?.id || null);
        const existingQuantity = before.items.filter(item => item.key !== key && matching(item)).reduce((sum,item)=>sum+item.quantity,0);
        const savedQuantity = after.items.filter(matching).reduce((sum,item)=>sum+item.quantity,0);
        if (savedQuantity !== quantity+existingQuantity) throw fail(messages.uncertain,true);
      }
      return after;
    },
    async loadEdit(key, variantId, accepts) {
      const cart = await this.read(), line=cart.items.find(item=>item.key===key);
      if (!line || !Array.isArray(accepts) || !accepts.includes(line.properties?._astra_profile) || String(line.variant_id)!==String(variantId)) throw fail(messages.stale);
      let draft=null;
      try {const saved=JSON.parse(sessionStorage.getItem(draftKey(key)));if(saved && Date.now()-saved.time<1800000 && saved.quantityBefore===line.quantity && saved.propertiesBefore===canonical(line.properties)) draft=saved.value;} catch (_) {}
      return {key,line,draft};
    },
    storeDraft(edit,value) {
      try {sessionStorage.setItem(draftKey(edit.key),JSON.stringify({time:Date.now(),quantityBefore:edit.line.quantity,propertiesBefore:canonical(edit.line.properties),value:{values:value.values,quantity:value.quantity}}));} catch (_) {}
    },
    clearDraft(key) {try {sessionStorage.removeItem(draftKey(key));} catch (_) {}},
    async saveEdit(edit,value) {
      if (String(value.id)!==String(edit.line.variant_id)) throw fail(messages.variant_changed);
      if (!value.properties || Object.keys(value.properties).some(key => key.startsWith('_'))) throw fail(messages.error);
      const owned = value.managedProperties || Object.keys(value.properties);
      if (!Array.isArray(owned) || owned.some(key => typeof key !== 'string' || !key || key.startsWith('_'))) throw fail(messages.error);
      const technical = value.technicalProperties || {};
      if (Object.keys(technical).some(key => !key.startsWith('_astra_'))) throw fail(messages.error);
      const removed = owned.filter(key => !Object.prototype.hasOwnProperty.call(value.properties,key));
      const after=await this.change(edit.key,value.quantity,{...value.properties,...technical},edit.line,removed);
      this.clearDraft(edit.key);
      try {sessionStorage.setItem('astra:cart:message',messages.saved);} catch (_) {}
      return after;
    },
    goToCart() {window.location.assign(config.cartUrl);}
  };
  async function updateLine(row,quantity) {
    if (pending || uncertain) return;
    if (!Number.isSafeInteger(quantity) || quantity < 0) {feedback(messages.quantity_error,true);return;}
    const key=row.dataset.key;
    pending=true;lock(true);feedback(messages.working);
    try {
      const before = await api.read(), original = before.items.find(item=>item.key===key);
      if (!original) throw fail(messages.stale);
      const after = await api.change(key,quantity,undefined,original);
      const matches = quantity === 0 ? [] : after.items.filter(item => item.variant_id === original.variant_id && canonical(item.properties) === canonical(original.properties) && (item.selling_plan_allocation?.selling_plan?.id || null) === (original.selling_plan_allocation?.selling_plan?.id || null));
      if (quantity > 0 && matches.length !== 1) throw fail(messages.uncertain,true);
      if (!window.theme?.Cart?.updateCart) throw fail(messages.uncertain,true);
      await window.theme.Cart.updateCart();
      const roots=Array.from(document.querySelectorAll('[data-astra-cart-line]'));
      const expected = matches[0];
      const remaining=roots.filter(item=>item.dataset.key===(expected?.key || key));
      if (quantity===0 ? remaining.length>0 : !remaining.length || remaining.some(item=>Number(item.dataset.quantity)!==expected.quantity)) throw fail(messages.uncertain,true);
      feedback(messages.updated);
    } catch (error) {uncertain=!!error.uncertain;feedback(error.message || messages.error,true);}
    finally {pending=false;lock(uncertain);}
  }
  document.addEventListener('click',event=>{
    const control=event.target.closest('[data-astra-remove],[data-astra-quantity-step]');
    if (control) {
      event.preventDefault();event.stopImmediatePropagation();
      const row=control.closest('[data-astra-cart-line]'), input=row.querySelector('[data-astra-cart-quantity]');
      const quantity=control.hasAttribute('data-astra-remove') ? 0 : Math.max(1,Number(input.value)+Number(control.dataset.astraQuantityStep));
      void updateLine(row,quantity);return;
    }
    if ((pending || uncertain) && event.target.closest('[data-astra-edit-link],[data-astra-checkout]')) {event.preventDefault();event.stopImmediatePropagation();}
  },true);
  document.addEventListener('change',event=>{
    if (!event.target.matches('[data-astra-cart-quantity]')) return;
    event.stopImmediatePropagation();
    const row=event.target.closest('[data-astra-cart-line]');
    if (Number(event.target.value)!==Number(row.dataset.quantity)) {
      if (Number(event.target.value)<1) {feedback(messages.quantity_error,true);return;}
      void updateLine(row,Number(event.target.value));
    }
  },true);
  document.addEventListener('submit',event=>{
    if (!event.target.matches('[data-astra-cart-form]')) return;
    if (event.target.querySelector('[data-astra-launch-cart-blocked]')) {event.preventDefault();event.stopImmediatePropagation();return;}
    const consent=event.target.querySelector('[data-astra-consent]');
    const dirty=Array.from(event.target.querySelectorAll('[data-astra-cart-quantity]')).some(input=>Number(input.value)!==Number(input.closest('[data-astra-cart-line]').dataset.quantity));
    if (pending || uncertain || dirty || (consent && !consent.checked)) {
      event.preventDefault();event.stopImmediatePropagation();feedback(pending ? messages.working : uncertain ? messages.uncertain : dirty ? messages.quantity_error : messages.consent_error,true);
      if (consent && !consent.checked) consent.focus();
    }
  },true);
  window.addEventListener('pageshow',()=>{
    if (document.querySelector('.astra-cart-page')) {
      try {const message=sessionStorage.getItem('astra:cart:message');if(message){feedback(message);sessionStorage.removeItem('astra:cart:message');}} catch (_) {}
    }
  });
})();

